import { NAV_ID, SHOW_ONLY_PUBLISHED } from '../../settings'
import type { FooterAttributes, NavbarAttributes } from '../../types/cms'
import type {
  CategoryPageAttributes,
  ComplaintsAttributes,
  ContactPageAttributes,
  CookiePolicyAttributes,
  FormSettingsAttributes,
  HomepageAttributes,
  StrapiEntity,
  StrapiListResponse,
  StrapiSingleResponse,
  TechnologyPageData,
} from '../../types/strapi'
import type { TechnologyPageAttributes } from '../../types/siteData'
import BaseRequest from './BaseRequest'
import {
  categoryPagePopulate,
  contactUsPopulate,
  footerPopulate,
  formSettingsPopulate,
  homepagePopulate,
  legalPagePopulate,
  technologyPagePopulate,
  technologyPagesListParams,
} from './populate'

const strapiData = <T>(body: { data?: T } | undefined): T | undefined =>
  body?.data

const publicationState = () => (SHOW_ONLY_PUBLISHED ? 'live' : 'preview')

class Request extends BaseRequest {
  static footer = () =>
    this.get<StrapiSingleResponse<FooterAttributes>>('footer', {
      populate: footerPopulate,
    })

  static navbar = () =>
    this.get<StrapiSingleResponse<NavbarAttributes>>(`menus/${NAV_ID}`, {
      nested: true,
      populate: '*',
    })

  static technologyPages = () =>
    this.get<StrapiListResponse<TechnologyPageAttributes>>('technology-pages', {
      publicationState: publicationState(),
      ...technologyPagesListParams,
    })

  static homepage = () =>
    this.get<StrapiSingleResponse<HomepageAttributes>>('homepage', {
      publicationState: publicationState(),
      populate: homepagePopulate,
    }).then(strapiData)

  static technologies = (slug: string) =>
    this.get<StrapiListResponse<TechnologyPageData>>('technology-pages', {
      publicationState: publicationState(),
      filters: { slug },
      populate: technologyPagePopulate,
    }).then(strapiData)

  static categories = (category: string, subCategory: string) =>
    this.get<StrapiListResponse<CategoryPageAttributes>>('category-pages', {
      filters: { category, subCategory },
      populate: categoryPagePopulate,
    }).then(strapiData)

  static cookiePolicy = () =>
    this.get<StrapiSingleResponse<CookiePolicyAttributes>>('cookie-policy', {
      populate: legalPagePopulate,
    }).then(strapiData)

  static complaints = () =>
    this.get<StrapiSingleResponse<ComplaintsAttributes>>('complaint', {
      populate: legalPagePopulate,
    }).then(strapiData)

  static contactUs = () =>
    this.get<{ data?: StrapiEntity<ContactPageAttributes> }>('contact-us', {
      populate: contactUsPopulate,
    }).then(strapiData)

  static formSettings = () =>
    this.get<StrapiSingleResponse<FormSettingsAttributes>>('form-setting', {
      publicationState: publicationState(),
      populate: formSettingsPopulate,
    }).then(body => strapiData(body)?.attributes ?? null)
}

export default Request
